// ⚠️ Editing this file? Read docs/legal-version-bump.md first.
// Substantive changes require bumping CURRENT_LEGAL_VERSION in
// src/shared/legal-version.js — otherwise users won't be re-prompted.

import { LegalPage, LegalProse, LegalSection } from '../components/LegalProse';

export default function PrivacyPolicy() {
  return (
    <LegalPage title="Ember Privacy Policy" dateline="Effective: October 29, 2026">

        <LegalProse>
          <p>
            Oriscen, Inc. (“Oriscen,” “we,” “us,” “our,”) provides Ember, an artificial
            intelligence (“AI”) thinking partner for the real situations of your own life, operating by
            telephone, by text message through your phone’s native messaging apps, through our mobile
            applications for Android and iOS devices, and through your web browser. Ember is an AI and not a person; the Services say so on every door — a
            standing line under the conversation on the web, the welcome text and the HELP reply on
            the text door, the spoken words that open every call — and the Services place crisis
            resources in front of a user whose words carry a risk of self-harm; the protocol is published at{' '}
            <a href="/safety" data-log="legal_safety_link">ember.space/safety</a>. You tell Ember
            about your own life situations; Ember responds, keeps context, and writes what you told
            as a private, first-person page for you (an “Ember”). Your conversations and Embers are
            not made public. Ember may use aggregated or deidentified information derived from them
            to generate public pages as described in Section 4. Three different things can happen
            to what you tell: it is
            used to serve you; unless you turn this off, eligible Embers, and descriptions of your
            situation that Ember writes from what you tell, may be used — with identifying details
            removed — to help Ember understand and respond to people in
            similar situations (“Background Experience,” see Section 3); and no one can read your Ember unless you separately
            say yes &mdash; if one of your Embers could help other people, Ember asks you once, and
            only if you say yes may Ember offer that Ember to another user in a similar situation,
            who reads that Ember and without your identity (see Section 4).
          </p>
          <p>
            This Privacy Policy explains how we collect, use and share the personal information
            that we may collect, when you visit the subdomains and websites on{' '}
            <a href="https://ember.space/" target="_blank" rel="noopener noreferrer">
              https://ember.space/
            </a>{' '}
            (the “Sites”), use our mobile applications for Android and iOS devices, become a
            Customer, contact us to obtain more information, send an opt-in text or SMS message to
            our platform, or otherwise directly through your use of our online platform and services
            (“Services”). A reference to “Oriscen,” “we,” “us,” or the
            “Company” is a reference to Oriscen and its relevant affiliates involved in the
            collection, use, sharing, or other processing of personal information.
          </p>
          <p>Please visit the following links to learn more about our Privacy Policy:</p>
          <ol>
            <li>Whose Personal Information We Collect</li>
            <li>Personal Information We Collect</li>
            <li>How We Use Your Personal Information</li>
            <li>How We Share Your Personal Information</li>
            <li>Cookies or Similar Technologies</li>
            <li>Privacy Rights &amp; Additional Disclosures for Certain U.S. Residents</li>
            <li>Children’s Privacy</li>
            <li>International Transfers of Data</li>
            <li>Change of Control</li>
            <li>Data Security and Retention</li>
            <li>Privacy Policy Updates</li>
            <li>Contact Information</li>
          </ol>

          <LegalSection title="1. Whose Personal Information We Collect">
            <p>
              Oriscen obtains information from various entities. These entities are defined below
              by what they are referred to in this Privacy Policy and when information is
              collected. Entities may overlap and are not exclusive categories.
            </p>
            <ul>
              <li>
                Website visitors: When you visit our Sites or contact us directly for more
                information.
              </li>
              <li>
                Customers: When you contact us or engage with our Services, including free and
                paid versions.
              </li>
            </ul>
            <p>
              <strong>Third Party Links and Websites.</strong> Our Sites and Services may contain
              links to and from websites of third parties. If you follow a link to any of these
              websites, please note that these websites have their own privacy policies and that
              we do not accept any responsibility or liability for these policies. Please check
              these policies before you submit any data to these websites.
            </p>
          </LegalSection>

          <LegalSection title="2. Personal Information We Collect">
            <p>
              <strong>Information You Give Us.</strong> You may give us information by signing up
              for an online account, entering information through our forms or surveys, inputting
              information while using our Services (including sharing information about yourself or
              other individuals via messages), or contacting us by phone or email for information
              or customer service.
            </p>
            <p>The categories of information include:</p>
            <ul>
              <li>
                Identifiers, such as real name, alias, postal address, unique personal
                identifier, online identifier, Internet Protocol (“IP”) address, email address,
                account name, or other similar identifiers.
              </li>
              <li>
                Categories of personal information described in Section 1798.80(e) of the
                California Civil Code, such as name, address, or telephone number.
              </li>
              <li>
                Commercial information, such as products or services purchased, obtained or
                considered, or other purchasing or consumer histories or tendencies.
              </li>
              <li>
                Internet or other electronic network activity information, such as browsing
                history, search history, and information regarding a consumer’s interaction with
                an Internet website, application or advertisement.
              </li>
              <li>Geolocation data.</li>
              <li>Electronic or similar information.</li>
              <li>
                Inferences drawn from your personal information to create a profile about you and
                your preferences, characteristics, psychological trends, predispositions,
                behavior, attitudes, intelligence, abilities, and aptitudes. In practice these are
                Ember’s readings of where you stand in the situation you are describing and how that
                changes over time, made from your own words in order to respond to you, to find
                related experience, to serve, with identifying details removed, as Background
                Experience and, for account holders, to write the weekly letter, and a reading of
                whether your words carry a risk of self-harm (see Section 3). They also include a
                few basic facts about you — your gender, your age or age range, what you do, your
                relationship status and where you live — taken from what you tell Ember or enter
                under About you in Settings, and used only so that the Embers written for you, and
                the pictures drawn for them, fit you, and, if you ask Ember to meet someone new, so that
                Ember introduces you only to someone who chose to meet people of your gender, and only to the people you chose to meet (Section 4). You can see,
                change or decline each of them in Settings. Whether your recent words carried a risk
                to yourself or someone else also decides, for a time, whether introductions and Rooms
                are offered to you.
              </li>
            </ul>
            <p>
              We do not collect biometric information: your voice is transcribed, and no
              voiceprint or other biometric template is extracted from it.
            </p>
            <p>The categories of sensitive personal information include:</p>
            <ul>
              <li>
                Contents of mail, email, and text messages you send to Ember or otherwise
                authorize us to process, and — where you speak with Ember by telephone or
                through your browser — the recording of that conversation and its transcript;
              </li>
              <li>
                Precise location information, if you choose to provide it or authorize us to
                access it; and
              </li>
              <li>
                Other sensitive information that you voluntarily include in your messages or
                requests. This covers all statutorily required categories of sensitive personal
                information under California law.
              </li>
            </ul>
            <p>
              When you input information while using our Services, including but not limited to
              sending messages, granting us access to third-party services, or instructing the
              Services to retrieve or act on information you provide, you acknowledge that you are
              responsible for ensuring you have the necessary rights, permissions, or authority to
              do so. We collect each category of sensitive personal information solely for the
              purposes outlined in Section 3.
            </p>

            <h3>Information We Automatically Collect.</h3>
            <ul>
              <li>
                Sites: Like many website operators, we collect information that your browser sends
                whenever you visit our Sites. This includes log data, such as your computer’s IP
                address, browser type, browser version, the pages of our Site that you visit, the
                time and date of your visit, the time spent on those pages and other statistics,
                and whether you reached our page via a social or email campaign.
              </li>
              <li>
                Services: Some of our Services will collect information such as IP addresses,
                log-in credentials, connectivity details and device and browser information,
                approximate or precise location information if enabled, usage logs, interaction
                timestamps, error reports, and information about how you interact with the
                Services. This is used for access control and management.
              </li>
            </ul>

            <p>
              <strong>Information We Collect from Third Parties.</strong> If you access our Sites
              or Services through third parties (e.g., Meta or Google), or if you share content
              from our Sites to a third-party social media service, the third-party service will
              send us information about you if the third-party service and your account settings
              allow such sharing. The information we receive will depend on the policies and your
              account settings with the third-party service.
            </p>
          </LegalSection>

          <LegalSection title="3. How We Use Your Personal Information">
            <p>We use your personal information for the following purposes:</p>
            <ul>
              <li>
                To provide you with the Services, which includes:
                <ul>
                  <li>maintaining and servicing accounts,</li>
                  <li>providing customer service,</li>
                  <li>processing and filling orders and transactions,</li>
                  <li>verifying customer information,</li>
                  <li>processing payments,</li>
                  <li>providing advertising or marketing services to you.</li>
                  <li>understanding your messages, prompts, instructions, and context;</li>
                  <li>
                    generating responses, suggestions, reminders, plans, or other AI-powered
                    outputs.
                  </li>
                </ul>
              </li>
              <li>
                To provide analytics services, such as analyzing customer usage to personalize and
                customize your experience across our Services.
              </li>
              <li>
                To improve and develop our Services and conduct research, for example to develop
                new features.
              </li>
              <li>
                To evaluate and improve the safety, reliability, and performance of our Services.
                We may use aggregated, deidentified, or otherwise privacy-protective information to
                improve the Services, including training and improving our AI models.
              </li>
              <li>To conduct market research and project planning.</li>
              <li>
                To detect security incidents, protect against fraudulent or other criminal
                activity, debug and repair errors, and maintain the overall quality and safety of
                our Sites and Services.
              </li>
              <li>
                To share your website visitor activity, through website cookies, with third-party
                marketing partners to market our Services and our Sites and other websites that
                you may visit.
              </li>
              <li>To fulfill our legal and financial obligations.</li>
              <li>To investigate and resolve disputes.</li>
            </ul>
            <p>
              <strong>Background Experience.</strong> To help Ember understand and respond to life
              situations, we may analyze and use eligible Embers, eligible descriptions that Ember
              writes from your conversations — where you stood in a situation you described and, if
              you told Ember later, how it went — and derived versions of them as background
              experience for the Services. Before an Ember or such a description is used in this way,
              identifying details may be removed,
              generalized, or otherwise transformed. Background Experience may be used to improve the
              relevance, reasoning, retrieval, matching, and responses provided by Ember in similar
              situations, including when Ember tells another user, in its own words and without
              identifying anyone, how things went for people in a similar situation.{' '}
              <strong>
                Background Experience does not, by itself, authorize Ember to display, quote, or
                provide your Ember or your conversations to another user.
              </strong>{' '}
              Access by another user requires a separate feature or permission described in the
              Services. You can disable Background Experience through your privacy settings (the
              “Help Ember understand similar situations” switch); doing so stops future background
              use of your Embers and of those descriptions. We do not use information for
              Background Experience where applicable law requires consent that we have not obtained
              or where the information is otherwise ineligible under our privacy rules.
            </p>
            <p>
              <strong>Marketing.</strong> We may use your personal information to contact you with
              newsletters, marketing, or promotional materials and other information that may be
              of interest to you. You may opt out of receiving any, or all, of the communications
              from us by following the unsubscribe instructions provided in any email we send, or
              you can contact us using the contact details provided at the bottom of this Privacy
              Policy. You will still continue to receive service-related messages concerning
              products and Services you have purchased (unless we have indicated otherwise). For
              more information about our mobile messaging services, please visit our{' '}
              <a href="/terms" data-log="legal_terms_link">Mobile Terms</a>.
            </p>
          </LegalSection>

          <LegalSection title="4. How We Share Your Personal Information">
            <p>
              <strong>To Other Users, Subject to Your Settings.</strong> We may share your
              information with other users or the public with your consent, and the settings you
              choose determine how much is shared. No other user can read your Ember unless you
              separately say yes. When one of your Embers could help other people, Ember asks you
              once whether it may offer that Ember to people in a similar situation; only if you say
              yes may Ember offer it to another user in a similar situation, who reads that one
              Ember in your words and without your name, voice, recording, transcript, or contact
              details, and you can close it again at any time. Background Experience (Section 3) is
              not sharing with another user: it never displays, quotes, or provides your Ember or
              your conversations to anyone. You may also choose to leave feedback or a
              testimonial, or to send your Ember to someone yourself with a link you can take back.
            </p>
            <p>
              <strong>A light/place.</strong> If your experience echoes with other users’
              experience, Ember may analyze, summarize or otherwise transform your information, to
              create a public page (a “light/place”) to reflect similar circumstances that you share
              with other users. The page will display only the aggregated or deidentified
              information, in a manner that could not reasonably be linked to you or your household,
              together with questions that visitors have typed on that page, shown without any
              identity after an automated check for identifying details and prohibited content.
            </p>
            <p>
              You can disable the above public page function through your privacy settings (the
              “Help Ember understand similar situations” switch); doing so stops your Embers from
              being used to generate public light/place pages in the future.
            </p>
            <p>
              <strong>Asking and answering questions in a letter from other users.</strong> During
              a conversation, Ember may summarize and generate a question as a letter (a “question
              letter”) to ask other users on your behalf, or you may receive a question letter from
              other users and can choose to answer it. With your consent, your question letter may be
              shared with invited users. If you choose to answer a question from another user’s
              question letter, your answer may be shared with that user who asked the question and
              other users invited to answer it. Your identity and contact information will not be
              displayed or shared.
            </p>
            <p>
              Questions and answers involving explicit sexual content, sexual assault, graphic
              violence, self-harm, or suicidal thoughts are not allowed. Please avoid inputting such
              content.
            </p>
            <p>
              <strong>Meet people: one-to-one introductions and Rooms.</strong> Once you have talked with Ember on a few
              different days, you may ask Ember to introduce you to someone new, a one-on-one chat
              with one other registered user and Ember (“chat”), or put up a topic, ask to join a
              topic someone else put up, or accept an invitation Ember offers you in a conversation
              to a small group chat on that topic with Ember (“Room”). Nothing is shared with
              another user until you take one of these steps, and you can stop looking, decline an
              introduction, or leave a chat or Room at any time.
            </p>
            <p>
              To meet someone new, you make a card: the name you go by (your first name to start,
              which you can change), a picture, a line Ember drafts from your conversations that you
              can edit or replace before anyone sees it, the topics you list, and whether you are
              happy to chat slowly. The person Ember introduces you to sees your card, your time
              zone, and a short introduction Ember writes from the two cards alone. In a Room, the
              other members see the name and picture you use there, the line you approve, and your
              Room messages. In a chat or Room, the other people also see what you write there.
              Your phone number, email, conversations with Ember, Embers, and Ember’s own
              impressions of you are not shown to other users. Your pronouns from About you are not
              shown on your card, but Ember may use them when it speaks of you to the person you
              are introduced to.
            </p>
            <p>
              To decide whom to introduce or whom to ask whether they would like to meet someone, to draft your card line, to decide whether a Room on
              a topic fits you, and whom to ask whether they would like to join a Room, Ember reads what it knows from your conversations (its notes on them
              and your recent messages), your card, your pronouns from About you (two people are
              introduced only when each fits whom the other chose to meet: anyone, or only women or only men), and your time zone (you
              are introduced only to people whose clocks are a few hours from yours). Ember
              may also tell you, in your conversation and without identifying anyone, the topics
              that people looking to meet someone have put on their cards. If you ask to meet someone new,
              Ember may also show your card to someone who has not asked but who it thinks would get
              along with you, and ask them whether they would like to meet you. While a Room you are in is
              waiting for people, Ember may also show its topic and the lines its members use there to
              someone who it thinks would enjoy it, and ask them whether they would like to join. This processing is done
              with the AI model providers listed below.
            </p>
            <p>
              Ember takes part in every chat and Room. It may read their messages to host and
              moderate them, may refer to them in its private conversations with their members, and,
              a week after a one-on-one chat opens, asks each of you privately how it is
              going. A chat or Room closes after a week with no messages; its members can still read
              it afterward. When you leave a Room you can choose to take your messages with you; if
              you do not, they stay for the other members to read. When your account is deleted,
              your cards, requests, memberships, and the messages you wrote in chats and Rooms are
              deleted.
            </p>
            <p>
              <strong>Affiliates and Service Providers.</strong> We share your information with
              our third-party service providers and any subcontractors as required to offer our
              Services. The service providers we use help us to:
            </p>
            <ul>
              <li>
                Run, operate, and maintain our Sites and Services through third-party platforms
                and software tools;
              </li>
              <li>Perform content moderation and crash analytics;</li>
              <li>
                Provide the artificial intelligence models that generate and evaluate the
                Services’ responses;
              </li>
              <li>
                Provide voice calling, speech recognition, and transcription for our phone and
                browser voice features;
              </li>
              <li>
                Generate the header image and the background music that accompany a story page,
                from a short description of its scene and mood;
              </li>
              <li>Run email and mobile messaging campaigns;</li>
              <li>
                Provide measurement services and targeted ads (you can opt out of these services
                at websites such as{' '}
                <a href="http://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer" data-log="legal_daa">
                  http://www.aboutads.info/choices
                </a>
                );
              </li>
              <li>Provide payment attribution; and</li>
              <li>Provide technical and customer support.</li>
            </ul>
            <p>
              As of the Effective Date these providers are: Render (hosting) and Cloudflare (content
              delivery); Anthropic, OpenAI, Google Cloud Vertex AI, Fireworks AI and Weights &amp;
              Biases (CoreWeave) (the AI models
              that generate and evaluate responses, write the Ember page and compute the retrieval
              signals that find related experience — configured so that our requests are not used
              to train their models and, where the provider offers it, not retained after the
              request); ElevenLabs
              (voice calling, speech recognition and transcription; the recording and transcript of
              a call are held there and deleted when your account is deleted); Google Firebase
              (phone-number sign-in and app notifications, and, in the mobile applications, Google
              Analytics for Firebase — analytics and advertising measurement: how the app is used
              and whether an install, a sign-up or a first conversation followed one of our
              advertisements; it may use the device's advertising identifier, which you can reset or
              delete in your device settings, and never receives the contents of a conversation);
              Linq (text messaging); Cloudinary (the photos you add); Stripe (payments); Meta,
              TikTok, Google Ads and Reddit Ads (measurement of our advertising — hashed
              identifiers, ad click identifiers and event names only, never the contents of a
              conversation, and only from a browser that accepted cookies or from the mobile
              applications); Google Analytics (aggregate usage statistics for our Sites, only from a
              browser that accepted cookies).
            </p>
            <p>
              Some external service providers may also collect information directly from you (for
              example, a payment processor may request your billing and financial information) in
              accordance with their own privacy policy. These external service providers do not
              share your financial information, like credit card number, with us, but may share
              limited information with us related to your purchase, like your zip code.
            </p>
            <p>
              <strong>Aggregated or Deidentified Data.</strong> We may use and disclose aggregated
              or otherwise deidentified information that does not identify and cannot reasonably be
              linked to an individual. We will maintain and use deidentified information in
              deidentified form and will not attempt to reidentify it, except as permitted or
              required by applicable law.
            </p>
            <p>
              Please kindly note that anonymity is a designed feature but not an absolute
              guarantee. Small peer circle environments carry limited residual re-identification
              risk. We do not warrant perfect anonymity in all scenarios.
            </p>
            <p>
              <strong>Third Party Marketing and Advertising Cookies.</strong> With respect to
              website cookies, we share your information with third-party marketing and
              advertising partners to market our products and Services on our Sites and other
              websites you may visit.
            </p>
            <p>
              <strong>Third Party Marketing.</strong> If you are one of our Customers, California
              Civil Code Section 1798.83 permits you to request information regarding the
              disclosure of your personal information to third parties for third parties’ direct
              marketing purposes. To make such a request, please contact us using the details in
              the “Contact Information” section below.
            </p>
            <p>
              <strong>Disclosures Required by Law.</strong> We may be required to disclose your
              data in response to lawful requests by public authorities, including meeting law
              enforcement requirements. We may be under a duty to disclose or share your personal
              information to comply with any legal obligation, to enforce or apply our terms and
              conditions and other agreements, to protect our rights, property, safety, or to
              protect the rights, property, or safety of others. This includes exchanging
              information with other companies and organizations for the purposes of fraud
              protection.
            </p>
          </LegalSection>

          <LegalSection title="5. Cookies or Similar Technologies">
            <p>
              To make our Sites and Services work properly, we use technologies such as cookies,
              web beacons, clear GIFs, and other means, such as the Meta Pixel, the TikTok Pixel,
              the Reddit Pixel, the Google tag (Google Ads and Google Analytics), and our product
              analytics tools, to collect information. The mobile applications do not use cookies;
              for the same purposes they use Google Analytics for Firebase, the device's advertising
              identifier (which you can reset or delete in your device settings) and, on Android,
              the Google Play install referrer that records which advertisement, if any, led to the
              install.
            </p>
            <h3>Cookies</h3>
            <p>
              We sometimes place small data files called cookies on your device. A cookie is a
              small text file that a website saves on your computer or mobile device when you visit
              the site. It enables the website to remember your actions and preferences (such as
              login, shopping cart, or other preferences) over a period of time, so you do not have
              to keep re-entering them whenever you come back to the site or browse from one page to
              another.
            </p>
            <p>
              You may wish to restrict the use of cookies. Please be aware that some of the
              features of our Sites may not function correctly if you disable cookies. Most modern
              browsers allow you to change your cookie settings. You can usually find these
              settings in the options or preferences menu of your browser. To understand these
              settings, the following links for commonly used browsers may be helpful:
            </p>
            <ul>
              <li>
                <a
                  href="https://support.google.com/chrome/answer/95647?hl=en&ref_topic=14666"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-log="legal_browser_cookies"
                >
                  Cookie settings in Chrome
                </a>
              </li>
              <li>
                <a
                  href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer?redirectslug=Cookies&redirectlocale=en-US"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-log="legal_browser_cookies"
                >
                  Cookie settings in Firefox
                </a>
              </li>
              <li>
                <a
                  href="https://support.microsoft.com/en-us/search?query=enable%2520cookies%2520in%2520edge"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-log="legal_browser_cookies"
                >
                  Cookie settings in Microsoft Edge
                </a>
              </li>
              <li>
                <a
                  href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-log="legal_browser_cookies"
                >
                  Cookie settings in Safari web
                </a>{' '}
                and{' '}
                <a
                  href="https://support.apple.com/en-us/HT201265"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-log="legal_browser_cookies"
                >
                  iOS
                </a>
              </li>
            </ul>
            <h3>Do Not Track</h3>
            <p>
              Some browsers provide you with a way to signal that you do not want your browsing
              activity to be tracked. The Sites honor the Global Privacy Control signal described
              below; they do not respond to the older Do Not Track (“DNT”) signal, for which there
              is no agreed meaning.
            </p>
            <p>
              California and certain other U.S. state residents may opt out of tracking
              technologies by broadcasting an Opt-Out Preference Signal, such as the Global Privacy
              Control (“GPC”) (on the browsers and/or browser extensions that support such a
              signal). To download and use a browser supporting the GPC browser signal, click here:{' '}
              <a href="https://globalprivacycontrol.org/orgs" target="_blank" rel="noopener noreferrer" data-log="legal_gpc">
                https://globalprivacycontrol.org/orgs
              </a>
              . If you choose to use the GPC signal, you will need to turn it on for each supported
              browser or browser extension you use. A browser sending the GPC signal loads none of
              the advertising cookies, and the signal, read by our servers, removes the stored
              advertising identifiers for that browser and — if you are signed in — for your
              account, so that no further measurement events about you are sent to an advertising
              platform.
            </p>
          </LegalSection>

          <LegalSection title="6. Privacy Rights & Additional Disclosures for Certain U.S. Residents">
            <p>
              Several jurisdictions grant state residents certain rights and disclosures. We
              provide the following information to further help you understand your potential
              privacy rights. If you would like to exercise any rights or inquire as to whether
              such rights are available to you, please email us at{' '}
              <a href="mailto:contact@oriscen.ai" data-log="legal_dsar_email">contact@oriscen.ai</a>.
            </p>
            <p>
              <strong>Request for Information.</strong> Residents of certain U.S. states have the
              right to know whether we are processing your personal information, and in some
              instances, you have the right to request that we disclose to you the categories
              listed below for the preceding 12 months. In responding to this right, we shall
              provide to you:
            </p>
            <ul>
              <li>The categories of personal information we collect about you.</li>
              <li>The categories of sources from which your personal information is collected.</li>
              <li>
                The business or commercial purpose(s) for collecting, selling, sharing, or
                disclosing your personal information, and the categories of personal information
                disclosed for such purpose(s).
              </li>
              <li>The categories of third parties with whom we share your personal information.</li>
              <li>
                The categories of personal information we have sold, if any, about you and the
                categories of third parties to whom your personal information was sold, by category
                or categories of personal information for each third party to whom the personal
                information was sold.
              </li>
              <li>The specific pieces of personal information we have collected about you.</li>
            </ul>
            <p>
              <strong>Request for Deletion.</strong> Residents of certain U.S. states have the
              right to request we delete your personal information. Once we receive your request
              and confirm your identity, we will delete or deidentify personal information and
              direct our service providers to take similar action. Deleting your account (see{' '}
              <a href="/delete-account" data-log="legal_delete_account_link">ember.space/delete-account</a>)
              erases everything the account holds after a 30-day window, including the recordings
              and transcripts of your calls held by our voice provider. Deleting a single Ember
              from Your Embers withdraws it from every place it appeared — Background Experience,
              any reader you had said yes to, the lights it stood at — while the conversation it
              was written from stays in your account until the account is deleted; you may also
              ask us to delete a particular conversation or part of one by contacting us.
            </p>
            <p>
              <strong>Request for Correction.</strong> Residents of certain U.S. states have the
              right to request the correction or rectification of inaccurate information in the
              resident’s personal information.
            </p>
            <p>
              <strong>Request for Portable Data.</strong> Residents of certain U.S. states have
              the right to receive, in certain circumstances, a portable format of their personal
              information that allows the data to be transmitted to another entity.
            </p>
            <p>
              <strong>Do Not Sell or Share My Personal Information.</strong> Residents of certain
              U.S. states have the right to opt out of the sale or sharing of the consumer’s
              personal information. We do not sell personal information. The one sharing we do is
              the advertising measurement described in Section 5; you may opt out of it by choosing
              “Reject” under “Cookie preferences” in the footer of the Sites, by sending the Global
              Privacy Control signal, or by contacting us using the details in the “Contact
              Information” section below.
            </p>
            <p>
              <strong>Limiting the Use of Sensitive Personal Information.</strong> Residents of
              California have the right to direct us to use or disclose sensitive personal
              information only for providing goods or services, or as otherwise minimally permitted
              under applicable law.
            </p>
            <p>
              We use sensitive personal information — the contents of what you tell Ember — to
              provide the Services: to respond to you, to read where you stand in the situation you
              describe, to find related experience, to write you, if you have an account, a weekly
              letter about what you have told Ember, and to read whether your words carry a risk of
              self-harm. We do not use it to infer characteristics about you for advertising or for
              any purpose other than providing the Services, maintaining security and complying with
              law. You retain the statutory right to limit our use of your sensitive personal
              information.
            </p>
            <p>
              <strong>Consumer Health Data and Background Experience.</strong> Some jurisdictions,
              including Washington, require affirmative, opt-in consent before consumer health data
              or other sensitive personal information may be processed for a purpose beyond
              providing the service you requested. Ember does not use such information for
              Background Experience where applicable law requires affirmative consent unless that
              consent has been obtained: an Ember that reveals it is kept out of Background
              Experience until you turn Background Experience on for that Ember yourself, on its
              own page, and the moment you do is recorded; a description of where you stood that
              Ember draws from your conversations is not used for Background Experience when it
              reveals such information. Turning it off again, or disabling
              Background Experience in Settings, stops such use from the next conversation on.
            </p>
            <p>
              <strong>Automated Processing and Profiling.</strong> Residents of certain U.S. states
              have the right to opt out of the processing of personal information for purposes of
              profiling in furtherance of decisions that produce legal or similarly significant
              effects concerning a consumer. However, we do not process your personal information
              for purposes of profiling in furtherance of decisions that produce legal or similarly
              significant effects.
            </p>
            <p>
              <strong>Right to Equal Service and Price.</strong> You have the right to receive
              equal service and price, even if you exercise a privacy right.
            </p>
            <p>
              <strong>Response Timing and Fees.</strong> We respond to verifiable consumer requests
              within 45 days, extendable another 45 days with prior notice. Two verifiable right to
              know requests per 12-month period are provided free of charge.
            </p>
            <p>
              <strong>Verification Process for Exercising Rights.</strong> To protect our
              consumers’ privacy, we verify privacy rights requests to ensure that only you (or
              your authorized agent) can exercise rights pertaining to your personal information
              with reasonable methods. As part of our verification process, we may request you to
              submit additional information.
            </p>
            <p>
              If you are using an authorized agent wishing to exercise rights on behalf of a state
              resident, please contact us using the information at the bottom of this Privacy
              Policy and provide us with a copy of the consumer’s written authorization designating
              you as their agent. We may need to verify your identity and place of residence before
              completing your rights request. We will not require you to provide more information
              than reasonably necessary to verify your identity or authority to make the request.
            </p>
            <p>
              <strong>Appeal Process.</strong> In certain cases, we may decline to take action on
              your request as permitted by law. Should we decline your request, we may provide you
              with a written explanation of the reasons for our decision and an opportunity to
              appeal the decision.
            </p>
            <p>
              If we deny your appeal, we may provide a written explanation of our decision with
              instructions on how to contact your local Attorney General or Department of Justice
              for further review, where permitted under certain U.S. state privacy laws.
            </p>
          </LegalSection>

          <LegalSection title="7. Children’s Privacy">
            <p>
              The Sites and Services are not directed at children under the age of 18, and Ember
              may not be suitable for some minors. The web and app sign-in ask you to confirm that
              you are 18 or over, and the text door says so when you join. We do not knowingly collect personal information from children under 18. If you become aware
              that a child has provided us with personal information, please contact us using the
              contact details provided at the end of this Privacy Policy, and we may delete or
              anonymize associated personal information in accordance with applicable law within a
              reasonable time.
            </p>
          </LegalSection>

          <LegalSection title="8. International Transfers of Data">
            <p>
              Any information that you provide to us may be accessed, shared, or processed by our
              offices, located in the United States, and service providers located in the United
              States, if such data transfer is necessary for the specific purpose for which you
              submitted your data (such as the provision of goods or services under a written
              contract). This may entail a transfer of your personal information across
              international borders. The data protection standards may differ and be lower than the
              standards enforced in your jurisdiction. We maintain appropriate safeguards as
              required by applicable law for any personal information transferred internationally.
            </p>
          </LegalSection>

          <LegalSection title="9. Change of Control">
            <p>
              Personal information may be transferred to a third party because of a sale,
              acquisition, merger, reorganization, or other change in control. If we sell, merge,
              or transfer any part of the business, part of the sale may include your personal
              information.
            </p>
          </LegalSection>

          <LegalSection title="10. Data Security and Retention">
            <p>
              We implement a variety of security measures to maintain the safety of your personal
              information when you enter, submit, or access your personal information. However, no
              website, application, or transmission can guarantee security. Thus, while we have
              established and maintain what we believe to be appropriate technical and
              organizational measures to protect the confidentiality, security, and integrity of
              personal information obtained through the Sites, we cannot ensure or warrant the
              security of any information you transmit to us.
            </p>
            <p>
              We retain information from or about you for so long as necessary to fulfill the
              purposes outlined in this Privacy Policy. When the information is no longer necessary
              for these purposes, we delete it or keep it in a form that does not identify you,
              unless we are required by law to keep this information for a longer period. When
              determining the retention period, we take into account various criteria, such as the
              type of products and services requested by or provided to you, the nature and length
              of our relationship with you, possible re-enrollment with our products or services,
              the impact on the services we provide to you if we delete some information from or
              about you, mandatory retention periods provided by law and the statute of
              limitations. In practice: your conversations, Embers and settings are kept for as long
              as your account exists and are erased 30 days after you delete it; an unclaimed guest
              telling is erased after 7 days; our AI model providers do not retain our requests
              beyond the request itself or, where the provider requires it, a short abuse-monitoring
              window; and the small set of compliance records described at{' '}
              <a href="/delete-account" data-log="legal_delete_account_link">ember.space/delete-account</a>{' '}
              outlives the account.
            </p>
          </LegalSection>

          <LegalSection title="11. Privacy Policy Updates">
            <p>
              This Privacy Policy may be periodically updated. When we make material changes, we
              may provide reasonable notice if required by applicable law. We encourage you to
              periodically review this Privacy Policy to ensure you are familiar with the most
              current version. Please review carefully before submitting personal information to our
              Sites or Services. The date this Privacy Policy was last updated is indicated at the
              top of the page. We will review and update this Privacy Policy at least once every 12
              months where required by applicable law.
            </p>
          </LegalSection>

          <LegalSection title="12. Contact Information">
            <p>
              If you wish to contact us or have any questions or complaints about this Privacy
              Policy, please contact us using the following information:
            </p>
            <p>
              <a href="mailto:contact@oriscen.ai" data-log="legal_dsar_email">contact@oriscen.ai</a>
            </p>
            <p>
              Oriscen, Inc.
              <br />
              2261 Market Street STE 86857
              <br />
              San Francisco, CA 94114
            </p>
          </LegalSection>
        </LegalProse>
    </LegalPage>
  );
}
